// Manage the shared purpose-based category directory; system categories are read-only and referenced history is preserved.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { localizedCategoryName } from '@/lib/category';
import { BackButton } from '@/components/BackButton';
import { AppDrawer } from '@/components/ui/AppDrawer';
import { ListCard } from '@/components/ui/ListCard';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/lib/toast';

interface CategoryRow {
  id: string;
  workspace_id: string | null;
  name: string;
  name_zh: string | null;
  icon: string | null;
  color: string | null;
  is_system: number;
  sort_order: number;
  description: string;
  description_zh: string;
}

interface CategoryFormValues {
  name: string;
  name_zh: string;
  icon: string;
  color: string;
}

const DEFAULT_COLOR = '#64748b';

const QUICK_EMOJI = [
  '🏢', '🏛️', '🛡️', '🏦', '💳', '📱', '🎓', '🎁', '🐾', '✈️',
  '🍔', '☕', '🍺', '🎬', '🏥', '💊', '🚗', '⛽', '🛒', '👕',
  '💼', '📚', '🎮', '💡', '🔧', '🧹', '🌱', '🎵', '📷', '🎨',
];

export function CategoriesPage() {
  const ws = useWorkspaceStore((s) => s.current);
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories', ws?.id],
    queryFn: () => api.get<CategoryRow[]>('/categories'),
    enabled: !!ws,
  });

  const [editing, setEditing] = useState<
    { mode: 'create' } | { mode: 'edit'; row: CategoryRow } | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['categories', ws?.id] });

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ category: CategoryRow; reused?: boolean }>('/categories', body),
    onSuccess: async (result) => {
      await invalidate();
      toast.success(result.reused
        ? i18n.language.startsWith('zh') ? `已使用现有分类：${localizedCategoryName(result.category, i18n.language)}` : `Using existing category: ${localizedCategoryName(result.category, i18n.language)}`
        : t('common.saved'));
      setEditing(null);
      setActionError(null);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : t('categories.saveError');
      setActionError(msg);
      toast.error(msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch<{ ok: true }>(`/categories/${id}`, body),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('common.saved'));
      setEditing(null);
      setActionError(null);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : t('categories.saveError');
      setActionError(msg);
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/categories/${id}`),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('common.deleted'));
      setEditing(null);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : t('categories.saveError');
      setActionError(msg);
      toast.error(msg);
    },
  });

  const systemCategories = categories?.filter((c) => c.is_system) ?? [];
  const customCategories = categories?.filter((c) => !c.is_system) ?? [];

  return (
    <div className="min-h-screen bg-muted/30 pb-8">
      <header className="border-b bg-background">
        <div className="container flex items-center gap-3 py-3">
          <BackButton />
          <h1 className="flex-1 truncate text-lg font-semibold">{t('categories.title')}</h1>
        </div>
      </header>

      <main className="container max-w-md space-y-3 py-4">
        <p className="rounded-xl border bg-card p-3 text-sm text-muted-foreground" data-testid="category-purpose-guidance">
          {i18n.language.startsWith('zh')
            ? '按实际消费用途分类，AI、手工账单和定期计划使用同一套类别。旅行使用群组归集，吃喝、购物和活动仍按各自用途分类。'
            : 'Classify by spending purpose. AI, manual receipts and recurring plans share this directory. Use groups for trips; food, shopping and activities keep their purpose categories.'}
        </p>
        {actionError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {actionError}
          </div>
        )}

        {isLoading && (
          <div className="rounded-2xl border bg-card p-8 text-center text-sm text-muted-foreground">
            {t('categories.loading')}
          </div>
        )}

        {/* Custom categories — editable */}
        <ListCard
          icon="🏷️"
          title={t('categories.custom')}
          summary={t('categories.customCount', { count: customCategories.length })}
          onAdd={() => {
            setEditing({ mode: 'create' });
            setActionError(null);
          }}
          addLabel={t('categories.addNew')}
          isEmpty={customCategories.length === 0}
          emptyState={
            <p
              className="text-center text-sm text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: t('categories.emptyCustom') }}
            />
          }
        >
          {customCategories.map((c) => (
            <div key={c.id} className="py-2">
              <CategoryReadRow
                cat={c}
                locale={i18n.language}
                onEdit={() => {
                  setEditing({ mode: 'edit', row: c });
                  setActionError(null);
                }}
              />
            </div>
          ))}
        </ListCard>

        {/* System categories — read-only */}
        <ListCard
          icon="📋"
          title={t('categories.system')}
          summary={
            t('categories.systemCount', { count: systemCategories.length }) +
            ' · ' +
            t('categories.readOnly')
          }
        >
          {systemCategories.map((c) => (
            <div key={c.id} className="flex items-center gap-2 py-2 text-sm">
              <span
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-base"
                style={{ background: c.color ?? '#e2e8f0' }}
              >
                {c.icon ?? '·'}
              </span>
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2">
                  {localizedCategoryName(c, i18n.language)}
                  {c.name_zh && !i18n.language.startsWith('zh') ? (
                    <span className="ml-2 text-muted-foreground">{c.name_zh}</span>
                  ) : null}
                </p>
                <CategoryDescription cat={c} locale={i18n.language} />
              </div>
            </div>
          ))}
        </ListCard>
      </main>

      {/* Category form drawer — page-level mount */}
      {editing && (
        <CategoryFormDrawer
          initial={editing.mode === 'edit' ? editing.row : null}
          isPending={createMutation.isPending || updateMutation.isPending || deleteMutation.isPending}
          onSubmit={(values) => {
            if (editing.mode === 'edit') {
              updateMutation.mutate({ id: editing.row.id, body: toPayload(values) });
            } else {
              createMutation.mutate(toPayload(values));
            }
          }}
          onClose={() => {
            setEditing(null);
            setActionError(null);
          }}
          onDelete={
            editing.mode === 'edit'
              ? async () => {
                  if (await confirmDialog({ title: i18n.language.startsWith('zh') ? '删除此分类？已有账单、计划或其他引用的分类会保留。' : 'Delete this category? Categories used by records or plans will be preserved.', destructive: true })) {
                    deleteMutation.mutate(editing.row.id);
                  }
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function CategoryReadRow({
  cat,
  locale,
  onEdit,
}: {
  cat: CategoryRow;
  locale: string;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-base"
        style={{ background: cat.color ?? '#e2e8f0' }}
      >
        {cat.icon ?? '·'}
      </span>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2">
          {localizedCategoryName(cat, locale)}
          {cat.name_zh && !locale.startsWith('zh') ? (
            <span className="ml-2 text-muted-foreground">{cat.name_zh}</span>
          ) : null}
        </p>
        <CategoryDescription cat={cat} locale={locale} />
      </div>
      <button
        onClick={onEdit}
        className="flex h-11 w-11 items-center justify-center rounded text-muted-foreground hover:bg-muted"
        aria-label={t('common.edit')}
      >
        ✏️
      </button>
    </div>
  );
}

function CategoryDescription({ cat, locale }: { cat: CategoryRow; locale: string }) {
  return <p className="mt-1 text-xs leading-relaxed text-muted-foreground" data-testid={`category-description-${cat.id}`}>
    {locale.startsWith('zh') ? cat.description_zh || cat.description : cat.description || cat.description_zh}
  </p>;
}

function CategoryFormDrawer({
  initial,
  isPending,
  onSubmit,
  onClose,
  onDelete,
}: {
  initial: CategoryRow | null;
  isPending: boolean;
  onSubmit: (values: CategoryFormValues) => void;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [nameZh, setNameZh] = useState(initial?.name_zh ?? '');
  const [icon, setIcon] = useState(initial?.icon ?? '');
  const [color, setColor] = useState(initial?.color ?? DEFAULT_COLOR);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t('categories.nameRequired'));
      return;
    }
    onSubmit({
      name: trimmedName,
      name_zh: nameZh.trim(),
      icon: icon.trim(),
      color: color.trim() || DEFAULT_COLOR,
    });
  }

  return (
    <AppDrawer
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={initial ? t('categories.editTitle') : t('categories.addTitle')}
      footer={
        <div className="space-y-2">
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="w-full rounded-xl bg-foreground py-3 text-sm font-semibold text-background disabled:opacity-50"
          >
            {isPending
              ? t('common.saving')
              : initial
                ? t('common.save')
                : t('categories.createButton')}
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              disabled={isPending}
              className="w-full rounded-xl py-2 text-xs text-rose-600 underline disabled:opacity-50"
            >
              {t('common.delete')}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs">
            <span className="mb-1 block text-muted-foreground">{t('categories.fieldName')}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('categories.fieldNamePlaceholder')}
              className="w-full rounded-md border bg-background px-2 py-2 text-sm"
            />
          </label>
          <label className="block text-xs">
            <span className="mb-1 block text-muted-foreground">{t('categories.fieldNameZh')}</span>
            <input
              type="text"
              value={nameZh}
              onChange={(e) => setNameZh(e.target.value)}
              placeholder={t('categories.fieldNameZhPlaceholder')}
              className="w-full rounded-md border bg-background px-2 py-2 text-sm"
            />
          </label>
        </div>

        <div className="grid grid-cols-[1fr,4rem] gap-2">
          <label className="block text-xs">
            <span className="mb-1 block text-muted-foreground">
              {t('categories.fieldIconLabel')}
            </span>
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder={t('categories.fieldIconPlaceholder')}
              maxLength={4}
              className="w-full rounded-md border bg-background px-2 py-2 text-base"
            />
          </label>
          <label className="block text-xs">
            <span className="mb-1 block text-muted-foreground">
              {t('categories.fieldColor')}
            </span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-full cursor-pointer rounded-md border bg-background"
            />
          </label>
        </div>

        <div>
          <span className="mb-1 block text-xs text-muted-foreground">
            {t('categories.quickEmoji')}
          </span>
          <div className="flex flex-wrap gap-1">
            {QUICK_EMOJI.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => setIcon(e)}
                className={
                  icon === e
                    ? 'rounded border-2 border-primary bg-background px-1.5 py-0.5 text-base'
                    : 'rounded border bg-background px-1.5 py-0.5 text-base hover:bg-muted'
                }
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-md border bg-background px-2 py-1.5 text-sm">
          <span className="text-xs text-muted-foreground">{t('categories.preview')}</span>
          <span
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-base"
            style={{ background: color || DEFAULT_COLOR }}
          >
            {icon || '·'}
          </span>
          <span className="flex-1 line-clamp-2">
            {name || t('categories.previewNamePlaceholder')}
            {nameZh ? <span className="ml-2 text-muted-foreground">{nameZh}</span> : null}
          </span>
        </div>

        {error && <div className="text-xs text-destructive">{error}</div>}
      </div>
    </AppDrawer>
  );
}

function toPayload(v: CategoryFormValues): Record<string, unknown> {
  return {
    name: v.name,
    name_zh: v.name_zh || null,
    icon: v.icon || null,
    color: v.color || null,
  };
}
